import { useProjectStore } from "entity-store/project-store/use-project-store";
import { useTemplateStore } from "entity-store/template-store/use-template-store";
import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  CoordinateBounds,
  useCoordinatesFromGPX,
} from "../hooks/use-coordinates";
import {
  PosterProject,
  defaultCoordinateLimit,
  PosterSizePreset,
  PosterLegend,
  StyledPoint,
  MapLabel,
  StaticLabel,
  Backdrop,
  GPSSource,
} from "./poster-project";
import { ProjectTemplate } from "./project-template";
import { defaultGPSSource, defaultProject } from "./default-project-style";

import { PointIconName, Point } from "./poster-types";
import { Coordinate } from "../hooks/use-coordinates";
import {
  MapStaticImageOptions,
  MapStyleBaseURL,
  PosterLegendPosition,
} from "./poster-project";
import { useMapBoxUtils } from "PosterEditor/hooks/use-mapbox-utils";
import { LineStyle, LinePattern } from "./poster-types";
import { PointStyle } from "./poster-types";
import { LngLat, LngLatBounds } from "mapbox-gl";
import { Thumbnail as MapStyleThumbnail } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-thumbnail-grid";
import { generateMapStyles } from "./poster-project";
import { v4 as uuid } from "uuid";
import { useGpsStore } from "entity-store/gps-store/use-gps-store";
import { useAccount } from "BEDROCK/account/use-account";
import {
  EditingPermissions,
  PointEditingPermissions,
} from "./editing-permissions";
import { isUserAuthenticated } from "BEDROCK/auth/auth";

interface PosterProjectContextType {
  project: PosterProject;

  setLastRenderID: (id: string) => void;
  markRendered: (rendered: boolean) => void;

  template: ProjectTemplate | undefined;
  setTemplate: (template: ProjectTemplate) => void;
  setTemplateThumbnail: (thumbnail: string) => void;

  isTemplateEditing: boolean;
  setEditingPermissions: (permissions: EditingPermissions) => void;

  // Turns a draft into a real project by setting `isTemplateDraft` to false.
  saveDraft: () => void;

  resetProject: () => void;

  selectionColor: string;

  mapStyleID: string;
  mapStyles: MapStyleThumbnail[];

  setCoordinateBounds: (bounds: CoordinateBounds) => void;

  setMapStyle: (styleID: string) => void;

  setMainRouteColor: (color: string) => void;
  setFlankRouteColor: (color: string) => void;

  setLineWidth: (width: number) => void;
  setLinePattern: (linePattern: LinePattern) => void;
  setLineDotDash: (dotDash: boolean) => void;

  setCoordinates: (coordinates: Coordinate[], sourceFileName: string) => void;
  setCoordinateLimit: (limit: number) => void;
  deleteCoordinate: (coordinate: Coordinate) => void;
  deleteAllCoordinates: () => void;
  gpsSource: GPSSource | undefined;

  styledPointAtCoordinate: (
    point: Coordinate
  ) => { point: StyledPoint; index: number } | undefined;
  setPointStyle: (point: Coordinate, style: PointStyle | undefined) => void;
  addNewStyledPoint: (coordinate: Coordinate) => StyledPoint;
  removeAllStyledPoints: () => void;
  setStyledPointEditingPermissions: (
    point: StyledPoint,
    permissions: PointEditingPermissions
  ) => void;

  getMapLabel: (id: string) => MapLabel | undefined;
  setMapLabel: (label: MapLabel) => void;
  addNewMapLabel: (coordinate: Coordinate) => MapLabel;
  removeMapLabel: (label: MapLabel) => void;

  getStaticLabel: (id: string) => StaticLabel | undefined;
  setStaticLabel: (label: StaticLabel) => void;
  addNewStaticLabel: (point: Point, relativeFontSize: number) => StaticLabel;
  removeStaticLabel: (label: StaticLabel) => void;

  getBackdrop: (id: string) => Backdrop | undefined;
  setBackdrop: (backdrop: Backdrop) => void;
  addNewBackdrop: (point: Point) => Backdrop;
  removeBackdrop: (backdrop: Backdrop) => void;
  setBackdrops: (backdrops: Backdrop[]) => void;

  setMapSize: (size: PosterSizePreset) => void;

  setBorderWidthMM: (width: number) => void;
  setBorderColor: (color: string) => void;

  staticMapImageURL: (options: MapStaticImageOptions) => string | null;
  staticQuadrantImageURL: (
    options: MapStaticImageOptions
  ) => { name: string; url: string }[];
}

const PosterProjectContext = createContext<
  PosterProjectContextType | undefined
>(undefined);

interface PosterProjectProviderProps {
  children: ReactNode;
  initialProject: PosterProject | undefined;
  // If this is passed in, it'll contain a reference to the same project as above.
  // the project above will be edited, and on save-to-template, this template.project should be updated.
  projectTemplate: ProjectTemplate | undefined;
}

const PosterProjectProvider = ({
  children,
  initialProject,
  projectTemplate,
}: PosterProjectProviderProps) => {
  const { loadProject, saveProject } = useProjectStore();
  const { saveTemplate } = useTemplateStore();
  const {
    loadCoordinates: loadCoordinatesFromStore,
    saveCoordinates: saveCoordinatesToStore,
    deleteCoordinates: deleteCoordinatesFromStore,
    copyGPSSourceFromPublicToUser,
  } = useGpsStore();
  const { account } = useAccount();

  //Todo: MIGRATE ANY REFERENCE TO PROJECT ROUTESOURCE TO THIS.
  //FOR REDUCING LIIMIT ETC, EVERY MENTION.
  const [gpsSource, setGPSSource] = useState<GPSSource | undefined>(undefined);

  const [project, setProject] = useState<PosterProject>(
    initialProject ?? defaultProject
  );
  const [template, setTemplate] = useState<ProjectTemplate | undefined>(
    projectTemplate
  );

  const [immediateGPSSourceSaveSemaphore, setImmediateGPSSourceSaveSemaphore] =
    useState<boolean>(false);

  const isTemplateEditing =
    account?.permissions === "admin" && template !== undefined;

  const saveToTemplate = () => {
    if (template) {
      template.project = project;
      setTemplate(template);
      saveTemplate(template, false);
    }
  };

  const saveTemplateToStore = (saveNow: boolean) => {
    if (template) {
      saveTemplate(template, saveNow);
    }
  };

  useEffect(() => {
    if (!gpsSource) {
      return;
    }
    if (project.isTemplateDraft) {
      return;
    }
    if (template) {
      //Save as public GPS source.
      saveCoordinatesToStore(
        gpsSource,
        gpsSource?.id ?? "",
        true,
        immediateGPSSourceSaveSemaphore
      );
      setImmediateGPSSourceSaveSemaphore(false);
    } else {
      //Save as private GPS source.
      saveCoordinatesToStore(
        gpsSource,
        gpsSource?.id ?? "",
        false,
        immediateGPSSourceSaveSemaphore
      );
      setImmediateGPSSourceSaveSemaphore(false);
    }
  }, [
    gpsSource,
    project.gpsSourceIDs,
    template,
    immediateGPSSourceSaveSemaphore,
  ]);

  const loadGPSSources = (project: PosterProject) => {
    const ids = project.gpsSourceIDs;
    if (!ids) {
      setGPSSource(defaultGPSSource);
      return;
    }
    const isPublic = template !== undefined || project.isTemplateDraft;

    ids.forEach((id) => {
      loadCoordinatesFromStore(id, isPublic).then((source) => {
        if (source) {
          setGPSSource(source);
        }
      });
    });
  };

  useEffect(() => {
    if (initialProject) {
      setProject(initialProject);
      loadGPSSources(initialProject);
    }
  }, []);

  const [mapStyleThumbnails, setMapStyleThumbnails] = useState<
    MapStyleThumbnail[]
  >([]);

  const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ?? "";
  const { getStaticMapURL, getQuadrantURLs } = useMapBoxUtils(accessToken);

  const selectionColor = () => {
    return "gray";
  };

  const {
    coordinates: testCoordinates,
    reduceCoordinates,
    reduceCoordinatesFavouringDistance,
    loading,
    error,
  } = useCoordinatesFromGPX(defaultCoordinateLimit);

  useEffect(() => {
    if (template) {
      // The project being edited is part of a template, so update it on the template rather than to
      // template storage.
      saveToTemplate();
    } else {
      project.updatedAt = new Date();
      saveProject(project);
    }
  }, [
    project,
    project.size,
    project.mapStyle,
    project.lineStyle,
    gpsSource?.allCoordinates,
    project.styledPoints,
    project.mapLabels,
    project.staticLabels,
    project.backdrops,
    project.legend,
  ]);

  useEffect(() => {
    if (!project.isTemplateDraft) {
      saveProject(project, true);
      if (gpsSource) {
        saveCoordinatesToStore(gpsSource, gpsSource?.id ?? "", false, true);
      }
    }
  }, [project.isTemplateDraft]);

  useEffect(() => {
    saveTemplateToStore(true);
  }, [template]);

  useEffect(() => {
    const path = window.location.pathname;
    const parts = path.split("/");
    const id = parts[parts.length - 1];
    if (id) {
      const project = loadProject(id);
      if (project) {
        setProject(project);
      }
    }
  }, []);

  useEffect(() => {
    generateMapStyleThumbnailsFromManifest();
  }, []);

  useEffect(() => {
    saveTemplateToStore(false);
  }, [template?.thumbnail]);

  const saveDraft = () => {
    // there is no template for a project started from a draft but not saved yet,
    // so copy over the gpsSourceID from template to project when it's uploaded.
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      isTemplateDraft: false,
    }));
  };

  const resetProject = () => {
    setProject(defaultProject);
  };

  const setLastRenderID = (id: string) => {
    setProject((prevProject) => ({
      ...prevProject,
      lastRenderID: id,
    }));
  };

  const markRendered = (rendered: boolean) => {
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: rendered,
    }));
  };

  const generateMapStyleThumbnailsFromManifest = async () => {
    const mapStyles = await generateMapStyles();
    const thumbnails: MapStyleThumbnail[] = [];
    for (const style of mapStyles) {
      thumbnails.push({
        id: style.mapBoxStyleID,
        src: `/pallete-components/map-thumbnails/${style.mapBoxStyleID}.png`,
        alt: `Map style ${style.mapBoxStyleID}`,
      });
    }
    setMapStyleThumbnails(thumbnails);
  };

  const reduceCoordinatesToLimit = useCallback(
    (limit: number) => {
      if (!gpsSource) {
        return;
      }
      const reduced = reduceCoordinates(gpsSource.allCoordinates, limit);
      setGPSSource((prev) => ({
        ...(prev ?? defaultGPSSource),
        displayedCoordinates: reduced,
        coordinateLimit: limit,
      }));
    },
    [gpsSource, reduceCoordinates]
  );

  // useEffect(() => {
  //   if (testCoordinates.length > 0) {
  //     setReduceCoordinates(testCoordinates);
  //   }
  // }, [testCoordinates]);

  const setReduceCoordinates = (
    coordinates: Coordinate[],
    name: string
  ): string => {
    const reduced = reduceCoordinatesFavouringDistance(
      coordinates,
      gpsSource?.coordinateLimit ?? defaultCoordinateLimit
    );

    const newID = uuid();

    const source: GPSSource = {
      id: newID,
      name: name,
      originDescription: "GPS",
      allCoordinates: coordinates,
      displayedCoordinates: reduced,
    };

    setProject((prev) => ({
      ...prev,
      gpsSource: [],
      isRendered: false,
      gpsSourceIDs: [newID],
    }));

    setGPSSource(source);

    return newID;
  };

  useEffect(() => {
    if ((gpsSource?.allCoordinates?.length ?? 0) > 0) {
      if (!project || gpsSource?.allCoordinates.length === 0) {
        reduceCoordinatesToLimit(defaultCoordinateLimit);
      }
    }
  }, [project, reduceCoordinates, reduceCoordinatesToLimit]);

  const setCoordinateLimit = (limit: number) => {
    // We still need a coordinateLimit state because the actual number of coordinates
    // we reduce to might not exactly be what the input requests.
    const coords = reduceCoordinatesFavouringDistance(
      gpsSource?.allCoordinates ?? [],
      limit
    );
    setGPSSource((prev) => ({
      ...(prev ?? defaultGPSSource),
      displayedCoordinates: coords,
    }));
  };

  const setMapSize = (size: PosterSizePreset) => {
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      size,
    }));
  };

  const deleteCoordinate = (coordinate: Coordinate) => {
    if (gpsSource) {
      const newCoordinates = gpsSource.displayedCoordinates.filter(
        (coord) => coord.lat !== coordinate.lat && coord.lng !== coordinate.lng
      );

      setGPSSource((prev) => ({
        ...(prev ?? defaultGPSSource),
        displayedCoordinates: newCoordinates,
      }));
    }

    const newStyledPoints = project.styledPoints.filter(
      (point) =>
        point.coordinate.lat !== coordinate.lat &&
        point.coordinate.lng !== coordinate.lng
    );

    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      styledPoints: [...newStyledPoints],
    }));
  };

  const deleteAllCoordinates = () => {
    const isPublic = template !== undefined;
    deleteCoordinatesFromStore(project.id, isPublic);
    setGPSSource(undefined);
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      gpsSources: [],
      styledPoints: [],
      sourceFileNames: [],
      mapLabels: [],
    }));
  };

  const styledPointAtCoordinate = (
    point: Coordinate
  ): { point: StyledPoint; index: number } | undefined => {
    const styledPoints = project.styledPoints ?? [];
    const index = styledPoints.findIndex((p) => {
      return p.coordinate.lat === point.lat && p.coordinate.lng === point.lng;
    });
    if (index === -1) {
      return undefined;
    }
    return { point: styledPoints[index], index };
  };

  const setPointStyle = (point: Coordinate, style: PointStyle | undefined) => {
    const styledPoints = project.styledPoints ?? [];
    const existing = styledPoints.findIndex((p) => {
      return p.coordinate.lat === point.lat && p.coordinate.lng === point.lng;
    });
    if (existing >= 0) {
      if (!style) {
        styledPoints.splice(existing, 1);
      } else {
        styledPoints[existing].style = style;
      }
    } else if (style) {
      styledPoints.push({
        coordinate: point,
        style,
        editingPermissions: project.editingPermissions.point,
      });
    }

    let lastUsedColors:
      | { mainColor: string; secondaryColor: string }
      | undefined;
    if (style) {
      lastUsedColors = {
        mainColor: style.mainColor,
        secondaryColor: style.secondaryColor ?? "white",
      };
    }
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      pointStyle: style ?? prevProject.pointStyle,
      styledPointsUpdateKey: Date.now().toString(),
      styledPoints,
      lastUsedStylePointColors: lastUsedColors,
    }));
  };

  const addNewStyledPoint = (coordinate: Coordinate) => {
    const point: StyledPoint = {
      coordinate: coordinate,
      style: { ...project.pointStyle },
      editingPermissions: project.editingPermissions.point,
    };
    setPointStyle(coordinate, point.style);
    return point;
  };

  const removeAllStyledPoints = () => {
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      styledPoints: [],
    }));
  };

  const setStyledPointEditingPermissions = (
    point: StyledPoint,
    permissions: PointEditingPermissions
  ) => {
    const styledPoints = project.styledPoints ?? [];
    const existing = styledPoints.findIndex((p) => {
      return (
        p.coordinate.lat === point.coordinate.lat &&
        p.coordinate.lng === point.coordinate.lng
      );
    });
    if (existing >= 0) {
      styledPoints[existing].editingPermissions = permissions;
    }
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      styledPoints,
    }));
  };

  const getMapLabel = (id: string): MapLabel | undefined => {
    let labels = project.mapLabels ?? [];
    return labels.find((p) => {
      return p.id === id;
    });
  };

  const setMapLabel = (label: MapLabel) => {
    let labels = project.mapLabels ?? [];
    const existing = labels.findIndex((p) => {
      return p.id === label.id;
    });
    if (existing >= 0) {
      labels[existing] = label;
    } else {
      labels.push(label);
    }
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      labels,
      labelUpdateKey: Date.now().toString(),
      mapLabelStyle: label.style,
    }));
  };

  const addNewMapLabel = (coordinate: Coordinate) => {
    const id = uuid();
    const label: MapLabel = {
      id,
      coordinate: coordinate,
      text: "",
      style: { ...project.mapLabelStyle },
      editingPermissions: project.editingPermissions.mapLabel,
    };
    setMapLabel(label);
    return label;
  };

  const removeMapLabel = (label: MapLabel) => {
    const labels = project.mapLabels.filter((l) => l.id !== label.id);
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      mapLabels: labels,
      labelUpdateKey: Date.now().toString(),
    }));
  };

  const getStaticLabel = (id: string): StaticLabel | undefined => {
    let labels = project.staticLabels ?? [];
    return labels.find((p) => {
      return p.id === id;
    });
  };

  const setStaticLabel = (label: StaticLabel) => {
    let labels = project.staticLabels ?? [];
    const existing = labels.findIndex((p) => {
      return p.id === label.id;
    });
    if (existing >= 0) {
      labels[existing] = label;
    } else {
      labels.push(label);
    }
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      staticLabels: labels,
      labelUpdateKey: Date.now().toString(),
      staticLabelStyle: label.style,
    }));
  };

  const addNewStaticLabel = (position: Point, relativeFontSize: number) => {
    const id = uuid();
    const label: StaticLabel = {
      id,
      position,
      text: "",
      description: "Label",
      style: { ...project.staticLabelStyle, fontSize: relativeFontSize },
      editingPermissions: project.editingPermissions.staticLabel,
    };
    setStaticLabel(label);
    return label;
  };

  const removeStaticLabel = (label: StaticLabel) => {
    const labels = project.staticLabels.filter((l) => l.id !== label.id);
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      staticLabels: labels,
      labelUpdateKey: Date.now().toString(),
    }));
  };

  const getBackdrop = (id: string) => {
    return project.backdrops.find((backdrop) => backdrop.id === id);
  };

  const setBackdrop = (backdrop: Backdrop) => {
    let backdrops = project.backdrops ?? [];
    const existing = backdrops.findIndex((b) => b.id === backdrop.id);
    if (existing >= 0) {
      backdrops[existing] = backdrop;
    } else {
      backdrops.push(backdrop);
    }
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      backdrops,
      backdropUpdateKey: Date.now().toString(),
      backdropStyle: backdrop.style,
    }));
  };

  const setBackdrops = (backdrops: Backdrop[]) => {
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      backdrops,
    }));
  };

  const addNewBackdrop = (point: Point) => {
    const id = uuid();
    const count = project.backdrops.length;
    const backdrop: Backdrop = {
      id,
      position: point,

      size: { width: 0.3, height: 0.5 },
      style: { ...project.backdropStyle },
      name: `Backdrop ${count + 1}`,
      editingPermissions: project.editingPermissions.backdrop,
    };
    setBackdrop(backdrop);
    return backdrop;
  };

  const removeBackdrop = (backdrop: Backdrop) => {
    const backdrops = project.backdrops.filter((b) => b.id !== backdrop.id);
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      backdrops,
    }));
  };

  const staticMapImageURL = (options: MapStaticImageOptions) => {
    return getStaticMapURL(options);
  };

  const staticQuadrantImageURL = (options: MapStaticImageOptions) => {
    return getQuadrantURLs(options);
  };

  const setCoordinates = (
    coordinates: Coordinate[],
    sourceFileName: string
  ) => {
    const gpsSourceID = setReduceCoordinates(coordinates, sourceFileName);

    setImmediateGPSSourceSaveSemaphore(true);
    setProject((prevProject) => ({
      ...prevProject,
      bounds: undefined,
      isRendered: false,
      gpsSourceIDs: [gpsSourceID],
      center: undefined,
      zoom: undefined,
    }));
  };

  const setMapStyle = (styleID: string) => {
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      mapStyle: MapStyleBaseURL + styleID,
    }));
  };

  const setMainRouteColor = (color: string) => {
    const style: LineStyle = { ...project.lineStyle, mainColor: color };
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      lineStyle: style,
    }));
  };

  const setFlankRouteColor = (color: string) => {
    const style: LineStyle = { ...project.lineStyle, flankColor: color };
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      lineStyle: style,
    }));
  };

  const setLinePattern = (pattern: LinePattern) => {
    let style: LineStyle = { ...project.lineStyle, mainPattern: pattern };
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      lineStyle: style,
    }));
  };

  const setLineDotDash = (dotDash: boolean) => {
    let style: LineStyle = { ...project.lineStyle, dotDash: dotDash };
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      lineStyle: style,
    }));
  };

  const setLineWidth = (width: number) => {
    let style = { ...project.lineStyle, width };
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      lineStyle: style,
    }));
  };

  const setCoordinateBounds = (bounds: CoordinateBounds) => {
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      bounds,
    }));
  };

  const setBorderWidthMM = (width: number) => {
    const borderStyle = { ...project.borderStyle, borderWidth: width };
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      borderStyle,
    }));
  };
  const setBorderColor = (color: string) => {
    const borderStyle = { ...project.borderStyle, borderColor: color };
    setProject((prevProject) => ({
      ...prevProject,
      isRendered: false,
      borderStyle,
    }));
  };

  const updateTemplate = (template: ProjectTemplate) => {
    setTemplate({ ...template });
  };

  const setTemplateThumbnail = (thumbnail: string) => {
    if (template) {
      setTemplate({ ...template, thumbnail });
    }
  };

  const setEditingPermissions = (editingPermissions: EditingPermissions) => {
    setProject((prevProject) => ({
      ...prevProject,
      editingPermissions,
    }));
  };

  const mapStyleID = project.mapStyle.replace(MapStyleBaseURL, "");

  return (
    <PosterProjectContext.Provider
      value={{
        mapStyleID,
        project,
        setLastRenderID,
        markRendered,
        template,
        setTemplate: updateTemplate,
        isTemplateEditing,
        setEditingPermissions,
        setTemplateThumbnail,
        saveDraft,
        resetProject,
        selectionColor: selectionColor(),
        setCoordinateBounds,
        setMapStyle,
        mapStyles: mapStyleThumbnails,
        setMainRouteColor,
        setFlankRouteColor,
        setLineWidth,
        setLinePattern,
        setLineDotDash,
        setCoordinateLimit,
        setCoordinates,
        gpsSource,
        setMapSize,
        setBorderColor,
        setBorderWidthMM,
        deleteCoordinate,
        deleteAllCoordinates,
        styledPointAtCoordinate,
        setPointStyle,
        addNewStyledPoint,
        removeAllStyledPoints,
        setStyledPointEditingPermissions,
        getMapLabel,
        setMapLabel,
        addNewMapLabel,
        removeMapLabel,
        getStaticLabel,
        setStaticLabel,
        addNewStaticLabel,
        removeStaticLabel,
        getBackdrop,
        setBackdrop,
        setBackdrops,
        addNewBackdrop,
        removeBackdrop,
        staticMapImageURL: staticMapImageURL,
        staticQuadrantImageURL,
      }}
    >
      {children}
    </PosterProjectContext.Provider>
  );
};

function usePosterProject() {
  const context = useContext(PosterProjectContext);
  if (!context) {
    throw new Error("usePosterProject must be used within an ProjectProvider");
  }
  return context;
}
export { PosterProjectProvider, usePosterProject };
